import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Save, Info, MapPin, CheckCircle2, ShieldAlert, Mail } from "lucide-react";

const EMPTY = {
  ragione_sociale: "", referente: "", partita_iva: "", codice_fiscale: "",
  indirizzo: "", citta: "", cap: "", provincia: "", pec: "", email: "", telefono: "", sdi: "",
  email_verified: false,
};

export const REQUIRED_STUDIO_FIELDS = [
  "ragione_sociale", "referente", "partita_iva", "indirizzo", "citta", "cap", "provincia", "pec", "email", "telefono",
];

const FIELDS = [
  { k: "ragione_sociale", label: "Ragione sociale / Denominazione studio", req: true, full: true },
  { k: "referente", label: "Nome amministratore referente", req: true },
  { k: "partita_iva", label: "Partita IVA", req: true, mono: true },
  { k: "codice_fiscale", label: "Codice Fiscale (facoltativo)", mono: true },
  { k: "sdi", label: "Codice SDI (facoltativo)", mono: true },
  { k: "citta", label: "Città", req: true },
  { k: "cap", label: "CAP", req: true },
  { k: "provincia", label: "Provincia", req: true },
  { k: "pec", label: "PEC", req: true },
  { k: "telefono", label: "Telefono", req: true },
];

const PROV_MAP = { "verona": "VR", "milano": "MI", "roma": "RM", "torino": "TO", "napoli": "NA", "firenze": "FI", "bologna": "BO", "venezia": "VE", "genova": "GE", "padova": "PD", "vicenza": "VI", "brescia": "BS", "bergamo": "BG" };

export const StudioProfileForm = ({ onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Autocompletamento indirizzo (Google Places — proxy backend, chiave lato server)
  const [addrQuery, setAddrQuery] = useState("");
  const [addrResults, setAddrResults] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const debRef = useRef(null);
  const boxRef = useRef(null);
  const sessRef = useRef((window.crypto?.randomUUID?.() || String(Math.random())));

  // Verifica email
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    api.get("/studio-profile")
      .then((r) => { const p = { ...EMPTY, ...(r.data?.profile || {}) }; setForm(p); setAddrQuery(p.indirizzo || ""); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAddrOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const searchAddress = (q) => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!q || q.trim().length < 3) { setAddrResults([]); return; }
    debRef.current = setTimeout(async () => {
      setAddrLoading(true);
      try {
        const r = await api.get(`/geocode/autocomplete`, { params: { q, session_token: sessRef.current } });
        setAddrResults(r.data?.suggestions || []); setAddrOpen(true);
      } catch { setAddrResults([]); }
      finally { setAddrLoading(false); }
    }, 300);
  };

  const pickAddress = async (s) => {
    setAddrOpen(false);
    try {
      const r = await api.get(`/geocode/details/${s.place_id}`, { params: { session_token: sessRef.current } });
      const d = r.data || {};
      const via = d.via || s.main_text || "";
      const citta = d.citta || "";
      let provincia = d.provincia || form.provincia;
      const key = (citta || "").toLowerCase();
      if (!d.provincia && PROV_MAP[key]) provincia = PROV_MAP[key];
      setForm((st) => ({ ...st, indirizzo: via, citta, cap: d.cap || st.cap, provincia }));
      setAddrQuery(via);
    } catch {
      setForm((st) => ({ ...st, indirizzo: s.main_text || st.indirizzo }));
      setAddrQuery(s.main_text || "");
    } finally {
      // nuova sessione di billing dopo ogni selezione
      sessRef.current = window.crypto?.randomUUID?.() || String(Math.random());
    }
  };

  const missing = REQUIRED_STUDIO_FIELDS.filter((k) => !(form[k] || "").toString().trim());

  const save = async () => {
    if (missing.length) { toast.error("Compila tutti i campi obbligatori."); return; }
    if (!form.email_verified) { toast.error("Verifica l'email ordinaria prima di salvare."); return; }
    setSaving(true);
    try {
      const r = await api.put("/studio-profile", form);
      toast.success("Dati dello studio salvati.");
      setForm((s) => ({ ...s, email_verified: r.data?.profile?.email_verified }));
      onSaved?.(r.data?.profile_complete);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const sendCode = async () => {
    if (!(form.email || "").trim()) { toast.error("Inserisci prima l'email ordinaria."); return; }
    setSendingCode(true);
    try {
      await api.post("/studio-profile/email/send-code", { email: form.email.trim() });
      setCodeSent(true);
      toast.success("Codice inviato all'email indicata.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Invio codice non riuscito"); }
    finally { setSendingCode(false); }
  };

  const verifyCode = async () => {
    if (!code.trim()) return;
    setVerifying(true);
    try {
      const r = await api.post("/studio-profile/email/verify", { code: code.trim() });
      setForm((s) => ({ ...s, email: r.data.email || s.email, email_verified: true }));
      setCodeSent(false); setCode("");
      toast.success("Email verificata con successo.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Verifica non riuscita"); }
    finally { setVerifying(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="studio-profile-form">
      <div className="flex items-start gap-2.5 rounded-lg border border-[#007BFF]/30 bg-[#007BFF]/[0.06] px-3.5 py-3 mb-5" data-testid="studio-informativa">
        <Info className="h-4 w-4 text-[#007BFF] mt-0.5 shrink-0" />
        <div className="text-[12px] leading-relaxed text-foreground/80">
          <span className="font-semibold text-foreground">Perché servono questi dati?</span> I dati del tuo studio sono
          necessari per mettere in funzione tutte le funzioni AI di Sedio: la lettura e il triage automatico delle email,
          l'apertura delle pratiche, l'assistente AI, i portali dei condòmini, gli SMS e la fatturazione degli collegamenti.
          Vengono inoltre usati come intestazione e firma nelle comunicazioni inviate ai condòmini e ai fornitori.
        </div>
      </div>

      <div className="space-y-3">
        {/* Ragione sociale + Nome amministratore */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Ragione sociale / Denominazione studio<span className="text-red-500"> *</span></label>
            <Input data-testid="studio-ragione_sociale" value={form.ragione_sociale} onChange={(e) => setForm((s) => ({ ...s, ragione_sociale: e.target.value }))} className="h-9 mt-1" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Nome amministratore referente<span className="text-red-500"> *</span></label>
            <Input data-testid="studio-referente" value={form.referente} onChange={(e) => setForm((s) => ({ ...s, referente: e.target.value }))} className="h-9 mt-1" />
          </div>
        </div>

        {/* Partita IVA + Codice Fiscale + SDI */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Partita IVA<span className="text-red-500"> *</span></label>
            <Input data-testid="studio-partita_iva" value={form.partita_iva} onChange={(e) => setForm((s) => ({ ...s, partita_iva: e.target.value }))} className="h-9 mt-1 font-mono uppercase" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Codice Fiscale (facoltativo)</label>
            <Input data-testid="studio-codice_fiscale" value={form.codice_fiscale} onChange={(e) => setForm((s) => ({ ...s, codice_fiscale: e.target.value }))} className="h-9 mt-1 font-mono uppercase" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Codice SDI (facoltativo)</label>
            <Input data-testid="studio-sdi" value={form.sdi} onChange={(e) => setForm((s) => ({ ...s, sdi: e.target.value }))} className="h-9 mt-1 font-mono uppercase" />
          </div>
        </div>

        {/* Indirizzo con autocompletamento (larghezza intera) */}
        <div className="relative" ref={boxRef}>
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />Indirizzo<span className="text-red-500"> *</span></label>
          <Input
            data-testid="studio-indirizzo"
            value={addrQuery}
            onChange={(e) => { setAddrQuery(e.target.value); setForm((s) => ({ ...s, indirizzo: e.target.value })); searchAddress(e.target.value); }}
            onFocus={() => addrResults.length && setAddrOpen(true)}
            placeholder="Inizia a scrivere l'indirizzo… (via, città)"
            className="h-9 mt-1" autoComplete="off" />
          {addrLoading && <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-3 top-[34px] text-muted-foreground" />}
          {addrOpen && addrResults.length > 0 && (
            <div className="absolute z-30 left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg max-h-64 overflow-auto" data-testid="addr-suggestions">
              {addrResults.map((s, i) => (
                <button key={s.place_id || i} type="button" data-testid={`addr-option-${i}`} onClick={() => pickAddress(s)}
                  className="w-full text-left px-3 py-2 hover:bg-secondary text-sm border-b border-border last:border-0">
                  <div className="font-medium truncate">{s.main_text || s.text}</div>
                  {s.secondary_text && <div className="text-xs text-muted-foreground truncate">{s.secondary_text}</div>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Città + CAP + Provincia */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {["citta", "cap", "provincia"].map((k) => {
            const f = FIELDS.find((x) => x.k === k);
            return (
              <div key={k}>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">{f.label}{f.req && <span className="text-red-500"> *</span>}</label>
                <Input data-testid={`studio-${k}`} value={form[k]} onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))} className={`h-9 mt-1 ${f.mono ? "font-mono uppercase" : ""}`} />
              </div>
            );
          })}
        </div>

        {/* PEC + Telefono */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {["pec", "telefono"].map((k) => {
            const f = FIELDS.find((x) => x.k === k);
            return (
              <div key={k}>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">{f.label}{f.req && <span className="text-red-500"> *</span>}</label>
                <Input data-testid={`studio-${k}`} value={form[k]} onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))} className={`h-9 mt-1 ${f.mono ? "font-mono uppercase" : ""}`} />
              </div>
            );
          })}
        </div>

        {/* Email ordinaria con verifica (larghezza intera) */}
        <div>
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />Email ordinaria<span className="text-red-500"> *</span></label>
          <div className="flex items-center gap-2 mt-1">
            <Input data-testid="studio-email" type="email" value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value, email_verified: false }))}
              className="h-9 flex-1" placeholder="posta@studio.it" />
            {form.email_verified ? (
              <span className="inline-flex items-center gap-1 text-xs text-green-600 font-semibold shrink-0" data-testid="email-verified-badge"><CheckCircle2 className="h-4 w-4" />Verificata</span>
            ) : (
              <Button type="button" data-testid="studio-email-verify-btn" onClick={sendCode} disabled={sendingCode || !(form.email || "").trim()}
                variant="outline" className="h-9 text-xs shrink-0">
                {sendingCode ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Verifica"}
              </Button>
            )}
          </div>
          {!form.email_verified && (
            <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-600" data-testid="email-unverified-note">
              <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>L'email ordinaria va verificata prima di salvare i dati. Riceverai un codice via email.</span>
            </div>
          )}
          {codeSent && !form.email_verified && (
            <div className="mt-2 flex items-center gap-2" data-testid="email-code-row">
              <Input data-testid="studio-email-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Codice a 6 cifre" className="h-9 w-40 font-mono tracking-widest" />
              <Button type="button" data-testid="studio-email-confirm-btn" onClick={verifyCode} disabled={verifying || code.length < 6}
                className="h-9 text-xs bg-[#007BFF] hover:bg-[#0069D9]">
                {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Conferma codice"}
              </Button>
              <button type="button" onClick={sendCode} disabled={sendingCode} className="text-[11px] text-muted-foreground hover:text-foreground underline">Reinvia</button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <Button data-testid="studio-save-btn" onClick={save} disabled={saving}
          className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salva dati studio
        </Button>
        {missing.length > 0 && (
          <span className="text-xs text-muted-foreground">{missing.length} campo/i obbligatori mancanti</span>
        )}
      </div>
    </div>
  );
};
